import { useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Pencil, Trash2, UserPlus, UserMinus, History } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtDate } from "@/pages/FirstAidKits";

const STATUS = {
  unassigned: { l: "Unassigned", c: "border-border opacity-70" },
  awaiting: { l: "Awaiting acceptance", c: "border-amber-400/60 text-amber-300" },
  accepted: { l: "Accepted", c: "border-[#83B68B]/60 text-[#83B68B]" },
};

export function KitCard({ kit, checklist, perms, highlight, onChanged, onEdit, onAssign, onUnassign, onDelete }) {
  const [confirm, setConfirm] = useState(false);
  const [qty, setQty] = useState({});
  const [busy, setBusy] = useState(false);
  const [showHist, setShowHist] = useState(false);
  useEffect(() => { setQty(kit.quantities || {}); }, [kit.quantities]);
  const canMaintain = kit.is_mine || perms?.can_manage;
  const st = STATUS[kit.status] || STATUS.unassigned;

  const call = async (fn, ok) => {
    setBusy(true);
    try { const { data } = await fn(); onChanged(data); if (ok) toast.success(ok); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const accept = () => call(() => api.post(`/first-aid/kits/${kit.id}/accept`, { confirm }), "Kit accepted — thank you");
  const saveContents = () => call(() => api.put(`/first-aid/kits/${kit.id}/contents`, { quantities: qty }), "Stock levels saved");
  const restock = (flag) => {
    let note = null;
    if (flag) { note = window.prompt("What's running low? (optional)"); if (note === null) return; }
    call(() => api.post(`/first-aid/kits/${kit.id}/restock`, { flag, note }), flag ? "Restock request sent to the club" : "Restock flag cleared");
  };
  const low = checklist.filter(i => (qty[i.key] ?? 0) < i.target).length;

  return (
    <div className={`card-tactical rounded-md p-5 space-y-4 ${highlight ? "ring-2 ring-[#83B68B]" : ""}`} data-testid={`fak-card-${kit.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-black text-2xl" data-testid="fak-number">{kit.number}</span>
            <span className={`text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border ${st.c}`} data-testid="fak-status">{st.l}</span>
            {kit.restock_flag && <span className="text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border border-red-500/60 text-red-400 inline-flex items-center gap-1" data-testid="fak-restock-badge"><AlertTriangle className="w-3 h-3" /> Needs restock</span>}
            {kit.is_stale && !kit.restock_flag && <span className="text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border border-amber-400/60 text-amber-300 inline-flex items-center gap-1" data-testid="fak-stale-badge"><Clock className="w-3 h-3" /> Check overdue</span>}
          </div>
          <div className="text-xs opacity-70 mt-1">
            {kit.assigned_user_name ? <>Held by <strong>{kit.assigned_user_name}</strong>{kit.team_name ? ` · ${kit.team_name}` : ""}</> : "Not yet assigned"}
            {kit.location ? ` · ${kit.location}` : ""}
          </div>
          <div className="text-[10px] opacity-50 mt-1">Last stock check: {kit.last_checked_at ? `${fmtDate(kit.last_checked_at)}${kit.last_checked_by ? ` by ${kit.last_checked_by}` : ""}` : "never"}{kit.accepted_at ? ` · accepted ${fmtDate(kit.accepted_at)}` : ""}</div>
        </div>
        {perms?.can_manage && (
          <div className="flex gap-1">
            <button type="button" onClick={onAssign} title={kit.assigned_user_id ? "Reassign" : "Assign to coach"} data-testid="fak-assign-btn" className="p-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B]"><UserPlus className="w-3.5 h-3.5" /></button>
            {kit.assigned_user_id && <button type="button" onClick={onUnassign} title="Unassign" data-testid="fak-unassign-btn" className="p-1.5 rounded border border-border hover:border-amber-400 hover:text-amber-300"><UserMinus className="w-3.5 h-3.5" /></button>}
            <button type="button" onClick={onEdit} title="Edit" data-testid="fak-edit-btn" className="p-1.5 rounded border border-border hover:border-[#83B68B]"><Pencil className="w-3.5 h-3.5" /></button>
            <button type="button" onClick={onDelete} title="Delete" data-testid="fak-delete-btn" className="p-1.5 rounded border border-border hover:border-red-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        )}
      </div>

      {kit.status === "awaiting" && kit.is_mine && (
        <div className="rounded-md border border-amber-400/40 bg-amber-400/5 p-4 space-y-3" data-testid="fak-accept-box">
          <div className="text-sm">Collected this kit? Confirm receipt below.</div>
          <label className="flex items-start gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={confirm} onChange={e => setConfirm(e.target.checked)} data-testid="fak-accept-check" className="mt-0.5" />
            <span>{perms?.accept_text}</span>
          </label>
          <button type="button" disabled={!confirm || busy} onClick={accept} data-testid="fak-accept-btn" className="btn-volt px-5 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40 inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5" /> Accept kit</button>
        </div>
      )}

      {kit.status === "accepted" && canMaintain && (
        <details className="group" data-testid="fak-contents" open={kit.is_mine}>
          <summary className="cursor-pointer text-[10px] tracking-widest uppercase opacity-70 hover:opacity-100 select-none">Contents checklist {low > 0 && <span className="text-amber-300">· {low} below target</span>}</summary>
          <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {checklist.map(i => {
              const v = qty[i.key] ?? 0; const under = v < i.target;
              return (
                <label key={i.key} className="flex items-center justify-between gap-2 text-xs">
                  <span className={under ? "text-amber-300" : ""}>{i.label} <span className="opacity-40">/ {i.target}</span></span>
                  <input type="number" min={0} max={999} value={v} onChange={e => setQty({ ...qty, [i.key]: Number(e.target.value) })} data-testid={`fak-qty-${i.key}`}
                    className={`w-16 h-8 rounded border bg-background px-2 text-right ${under ? "border-amber-400/60" : "border-border"}`} />
                </label>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2 mt-4">
            <button type="button" disabled={busy} onClick={saveContents} data-testid="fak-save-contents" className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase disabled:opacity-40">Stock checked today</button>
            {kit.restock_flag
              ? <button type="button" disabled={busy} onClick={() => restock(false)} data-testid="fak-restock-clear" className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase">Restocked — clear flag</button>
              : <button type="button" disabled={busy} onClick={() => restock(true)} data-testid="fak-restock-flag" className="px-4 py-2 rounded border border-red-500/50 text-red-400 text-[10px] tracking-widest uppercase hover:bg-red-500/10">Needs restock</button>}
            <a href="/app/kit" className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase opacity-70 hover:opacity-100" data-testid="fak-order-link">Order supplies</a>
          </div>
          {kit.restock_note && <div className="text-xs opacity-70 mt-2">Restock note: {kit.restock_note}</div>}
        </details>
      )}

      {(kit.history || []).length > 0 && (
        <div>
          <button type="button" onClick={() => setShowHist(s => !s)} data-testid="fak-history-toggle" className="text-[10px] tracking-widest uppercase opacity-50 hover:opacity-100 inline-flex items-center gap-1"><History className="w-3 h-3" /> History ({kit.history.length})</button>
          {showHist && (
            <ul className="mt-2 space-y-1 text-[11px] opacity-70" data-testid="fak-history">
              {[...kit.history].reverse().map((h, i) => <li key={`${h.at}-${i}`}>{fmtDate(h.at)} · <span className="uppercase tracking-wider">{h.action.replace("_", " ")}</span> · {h.by_name}{h.note ? ` — ${h.note}` : ""}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
